import { useEffect } from 'react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import { SparklesIcon, ShieldCheckIcon, UserGroupIcon, GlobeAltIcon, TruckIcon, KeyIcon } from '@heroicons/react/24/outline';

export default function About() {
  useEffect(() => {
    // Empty scroll effect as specified
  }, []);

  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen selection:bg-red-600 selection:text-white antialiased overflow-x-hidden">
      <Navbar />

      {/* Section 1: Company Write-up */}
      <section className="py-20 px-6 sm:px-8 bg-gradient-to-b from-white to-gray-100/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-8 w-1.5 bg-red-600 rounded"></div>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-blue-950 tracking-tight font-sans">
              ABOUT OUR COMPANY
            </h1>
          </div>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed font-light text-justify">
            Sajies Global Synergy Limited is a registered and trusted automotive expert with over 15 years of industry-leading experience in the importation and sales of both brand-new and high-quality foreign used vehicles. Our reputation for excellence has been built on a solid foundation of professional values, reliable logistics, and genuine customer care.
            <br /><br />
            We specialize not only in premium vehicle sales but also in advanced automobile upgrades, modifications, and customization options. Whether you are a private car owner, business fleet operator, or professional dealer, we provide customized solutions to get you on the road with total peace of mind.
            <br /><br />
            Our team of engineers and sourcing agents is fully committed to delivering vehicles and after-sales support that exceed expectations, making Sajies Global Synergy Limited your premium destination for everything automotive in Nigeria.
          </p>
        </div>
      </section>

      {/* Section 2: Mission and Vision */}
      <section className="py-20 px-6 sm:px-8 bg-white border-y border-gray-100">
        <div className="flex flex-col lg:flex-row items-center max-w-5xl mx-auto gap-12">
          {/* Custom Styled Responsive Framed Image */}
          <div className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 flex-shrink-0 relative">
            <div className="absolute inset-0 bg-blue-900 rounded-full translate-x-2.5 translate-y-2.5 shadow-xl opacity-10"></div>
            <img
              src="/img/sajies_car2.jpg"
              alt="Sajies premium automobile showcasing"
              className="w-full h-full object-cover rounded-full border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] relative z-10"
            />
          </div>
          <div className="flex-grow space-y-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-blue-950 font-sans tracking-tight mb-3">
                OUR MISSION
              </h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-light">
                To provide reliable, high-quality automobiles and auto parts that enhance mobility and deliver superior value to our customers, while maintaining integrity, innovation, and customer satisfaction at the core of our services.
              </p>
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-blue-950 font-sans tracking-tight mb-3">
                OUR VISION
              </h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-light">
                To be the most trusted and preferred automobile partner in Africa — empowering individuals and businesses through exceptional vehicle solutions, customer-centric services, and global standards in automotive excellence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Services */}
      <section className="py-20 px-6 sm:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="p-8 mb-12 text-center bg-gradient-to-r from-blue-950 to-slate-900 text-white rounded-2xl shadow-lg relative overflow-hidden">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-sans text-white mb-3">
              WHAT WE DO
            </h2>
            <p className="text-sm sm:text-base text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
              At Sajies Global Synergy Limited, we offer premium services to cater for all your automotive needs. Our goal is to deliver complete satisfaction from selection to delivery.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <span className="text-3xl">🚗</span>
              <h3 className="text-lg sm:text-xl font-bold text-blue-950 tracking-tight mt-3 mb-2 font-sans">
                Vehicle Importation & Sales
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-light leading-relaxed">
                We import and sell brand-new and certified foreign used vehicles (Tokunbo) from trusted global brands. Whether you're looking for luxury crossovers, SUVs, sedans, or commercial trucks, we provide options structured to fit your budget.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <span className="text-3xl">🛠</span>
              <h3 className="text-lg sm:text-xl font-bold text-blue-950 tracking-tight mt-3 mb-2 font-sans">
                Consultation & Global Sourcing
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-light leading-relaxed">
                Let us help you choose the right vehicle or parts. We provide expert advice and direct sourcing solutions from global suppliers to make sure you get exactly what you need — completely hassle-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Why Choose Us (Upgraded to a fully responsive, wrap-safe layout grid) */}
      <section className="py-20 px-6 sm:px-8 bg-blue-950 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-radial-gradient from-red-600/5 to-transparent pointer-events-none"></div>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3 text-center text-white tracking-tight font-sans">
            WHY CHOOSE US
          </h2>
          <p className="text-sm sm:text-base text-gray-300 mb-12 text-center font-light max-w-xl mx-auto leading-relaxed">
            With over 15 years of excellence, Sajies Global Synergy Limited stands out in Nigeria’s automotive market. Here's why clients trust us:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Experience & Expertise", desc: "Decades of active operation means you get reliable services, deep market insights, and expert vehicle recommendations.", icon: <SparklesIcon className="h-6 w-6 text-red-500" /> },
              { title: "Quality Assurance", desc: "We guarantee genuine vehicles and parts. Every vehicle undergoes rigorous pre-purchase inspections before shipment.", icon: <ShieldCheckIcon className="h-6 w-6 text-red-500" /> },
              { title: "Customer-Centric", desc: "We focus on your requirements, providing personalized guidance on registrations, custom clearing, and financing.", icon: <UserGroupIcon className="h-6 w-6 text-red-500" /> },
              { title: "Global Supplier Network", desc: "Our close international dealer partnerships allow us to source premium vehicles at competitive price margins.", icon: <GlobeAltIcon className="h-6 w-6 text-red-500" /> },
              { title: "Timely Delivery", desc: "We coordinate swift shipping and customs clearances, securing your vehicle in safe, timely turnarounds.", icon: <TruckIcon className="h-6 w-6 text-red-500" /> },
              { title: "Premium Sourcing", desc: "Direct imports of well-maintained foreign used and brand-new cars sourced directly from Germany, US, and Canada.", icon: <KeyIcon className="h-6 w-6 text-red-500" /> }
            ].map((card, idx) => (
              <div 
                key={idx} 
                className="bg-white p-6 rounded-xl shadow-md border border-gray-100 flex flex-col justify-between hover:scale-[1.02] transition-transform duration-300"
              >
                <div>
                  <div className="bg-red-50 p-2 rounded-lg w-fit mb-4">{card.icon}</div>
                  <h3 className="text-lg font-bold text-blue-950 font-sans tracking-tight mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-light">
                    {card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
